// FARM-05 data-source labels. Pure functions of the backend-emitted
// provenance tags (ag.sources, block.source) only — they must never branch
// on farm id or name, so the foliage farm can never be mislabeled as
// station-measured (T-01-10).

import type { AgBlockEntry, AgPayload } from '../types';

// Station kind → { name, ending } used to build the non-gridOnly sentence
// below. Derived only from the backend-emitted stationKind — never from farm
// id or name (FARM-05) — so the label can't drift when the LoRa station
// moves between farms.
const STATION_LABEL: Record<'ambient-pws' | 'lora', { name: string; ending: string }> = {
  'ambient-pws': { name: 'Ambient', ending: 'feeds the current conditions above.' },
  lora: { name: 'LoRa (LoraDash)', ending: 'feeds the current conditions above.' },
};

export function gridSourceLabel(ag: AgPayload): string {
  if (ag.sources.gridOnly) {
    return 'Forecast grid only — no station on this farm yet. Values are modeled, not measured.';
  }
  const station = STATION_LABEL[ag.farm.stationKind as 'ambient-pws' | 'lora'];
  return `Modeled from the Open-Meteo forecast grid. ${ag.farm.name} station (${station.name}) ${station.ending}`;
}

export function cimisSourceLabel(ag: AgPayload): string {
  const cimis = ag.sources.cimis;
  if (!cimis) {
    return 'ET₀ from the Open-Meteo forecast grid (CIMIS unavailable).';
  }
  if (cimis.nearby) {
    return `ET₀ measured at CIMIS #${cimis.id} (nearby) — ${cimis.name}, ${cimis.city}. Forecast values from Open-Meteo.`;
  }
  return `ET₀ measured at CIMIS #${cimis.id} (${cimis.name}, ${cimis.city}). Forecast values from Open-Meteo.`;
}

export function blockSourceLabel(block: AgBlockEntry, ag: AgPayload): string {
  const cimis = ag.sources.cimis;
  let label: string;
  if (block.source === 'cimis+forecast-grid') {
    label = `Next 7 days: Open-Meteo forecast grid · last 7 days: CIMIS #${cimis?.id ?? ''}${
      cimis?.nearby ? ' (nearby)' : ''
    }`;
  } else {
    label = 'Next 7 days: Open-Meteo forecast grid · no measured ET₀ available';
  }
  if (ag.sources.gridOnly) label += ' · no station on this farm yet';
  return label;
}
